<script>
import { mapGetters } from 'vuex'
import { EventBus } from '@/modules/plugins/modules/event'

export default {
    name: 'Modal',

    props: {
        closable: {
            type: Boolean,
            default: true,
        },
        value: Boolean,
        hideHeader: Boolean,
        scroll: Boolean,
        lg: Boolean,
        sm: Boolean,
        show: Boolean,
        menu: Boolean,
        scrollId: undefined,
        closeClass: {
            type: String,
            default: 'btn-secondary btn-xs',
        },
    },

    data() {
        return {
            originalPlace: null,
            contentHeight: null,
            open: false,
            unsubscribe: null,
            autoScroll: false,
            watcher: null,
        }
    },

    computed: {
        ...mapGetters({
            smDevice: 'smDevice',
            lgDevice: 'lgDevice',
            isApple: 'isApple',
            modalsOpen: 'modalsOpen',
            blockOverlay: 'blockOverlay',
        }),
        name() {
            return `modal-name-${this._uid}`
        },
        isActive() {
            return this.modalsOpen.length
                ? this.name === this.modalsOpen[this.modalsOpen.length - 1].name
                : false
        },
    },

    watch: {
        value: {
            immediate: true,
            handler(openValue) {
                if (openValue && !this.open) this.openModal()
                else if (!openValue && this.open) this.closeModal()
            },
        },
    },

    mounted() {
        this.originalPlace = this.$parent.$el
    },

    beforeDestroy() {
        this.closeModal(true, true)
    },

    methods: {
        calcInnerHeight() {
            this.$nextTick(() => {
                const headerHeight = this.$refs['modalHeader']
                    ? this.$refs['modalHeader'].getBoundingClientRect().height
                    : 0
                const footerHeight = this.$refs['modalFooter']
                    ? this.$refs['modalFooter'].getBoundingClientRect().height
                    : 0
                const offsetY = (this.$refs.dialog?.offsetTop || 32) * 2
                if (this.lgDevice)
                    this.contentHeight =
                        window.innerHeight -
                        (this.lgDevice ? offsetY : 0) -
                        headerHeight -
                        footerHeight
                else
                    this.contentHeight =
                        window.innerHeight -
                        headerHeight -
                        footerHeight -
                        (this.lgDevice ? offsetY : 0)
            })
        },
        openModal() {
            if (!this.open) {
                this.$set(this, 'open', true)
                this.reposition()
                this.$store.dispatch('modalOpen', {
                    name: this.name,
                    closable: this.closable,
                })
                this.calcInnerHeight()
                EventBus.$on('closeAllModals', this.closeModal)
                window.addEventListener('resize', this.calcInnerHeight)
                if (!this.value) this.$emit('input', true)
                this.watcher = this.$watch('closable', (closable) => {
                    this.$store.dispatch('modalRefresh', {
                        name: this.name,
                        closable: closable,
                    })
                })
            }
        },
        closeModal(emit = true, force = false) {
            if (this.open && (this.isActive || force)) {
                this.$set(this, 'open', false)
                this.reposition()
                this.$store.dispatch('modalClose', this.name)
                if (emit) this.$emit('closed')
                EventBus.$off('closeAllModals', this.closeModal)
                window.removeEventListener('resize', this.calcInnerHeight)
                if (this.value) this.$emit('input', false)
                if (this.watcher) {
                    this.watcher()
                    this.watcher = null
                }
            }
        },
        closeModalInner() {
            if (this.isActive && this.isActive) {
                this.closeModal(false)
                this.$emit('close')
                if (this.value) this.$emit('input', false)
            }
        },
        reposition() {
            if (this.open) {
                this.$root.$el.appendChild(this.$mount().$el)
            } else {
                /* eslint no-empty: 0 */
                try {
                    this.originalPlace.appendChild(this.$mount().$el)
                } catch (e) {}
            }
        },
    },

    render(h) {
        return this.open || this.show
            ? h(
                  'transition',
                  {
                      attrs: {
                          name: this.open
                              ? this.menu
                                  ? 'modal-up'
                                  : 'modal-fade-up'
                              : this.menu
                              ? 'modal-down'
                              : 'modal-fade',
                      },
                  },
                  [
                      h(
                          'div',
                          {
                              class: [
                                  'modal',
                                  {
                                      'mobile-view': this.smDevice && !this.menu,
                                      'not-active': !this.isActive,
                                  },
                              ],
                              directives: this.show
                                  ? [{ name: 'show', value: this.open }]
                                  : undefined,
                              attrs: {
                                  tabindex: -1,
                                  role: 'dialog',
                              },
                              on: {
                                  click: (e) => {
                                      if (!this.isActive) {
                                          e.stopPropagation()
                                          e.preventDefault()
                                      }
                                  },
                              },
                          },
                          [
                              h(
                                  'div',
                                  {
                                      class: [
                                          'modal-dialog',
                                          {
                                              menu: this.menu,
                                              'modal-dialog-scrollable':
                                                  this.scroll || this.autoScroll,
                                              'ios-device': this.isApple,
                                              'modal-lg': this.lg && !this.smDevice,
                                              'modal-sm': this.sm && !this.smDevice,
                                          },
                                      ],
                                      ref: 'dialog',
                                  },
                                  [
                                      h(
                                          'div',
                                          {
                                              class: 'modal-content',
                                          },
                                          [
                                              (this.closable || this.$slots.header) &&
                                              !this.hideHeader
                                                  ? h(
                                                        'div',
                                                        {
                                                            class: ['modal-header', {}],
                                                            ref: 'modalHeader',
                                                        },
                                                        [
                                                            this.$slots.header,
                                                            this.closable && !this.blockOverlay
                                                                ? h(
                                                                      'button',
                                                                      {
                                                                          class: `close-modal btn btn-icon rounded-circle ${this.closeClass}`,
                                                                          on: {
                                                                              click: this
                                                                                  .closeModalInner,
                                                                          },
                                                                      },
                                                                      [
                                                                          h('icon', {
                                                                              props: {
                                                                                  icon: 'fa-solid fa-times',
                                                                              },
                                                                          }),
                                                                      ]
                                                                  )
                                                                : null,
                                                        ]
                                                    )
                                                  : null,
                                              this.$slots.body
                                                  ? h(
                                                        'div',
                                                        {
                                                            class: ['modal-body'],
                                                            attrs: {
                                                                id: this.scrollId,
                                                            },
                                                            ref: 'scrollContainer',
                                                            style: {
                                                                maxHeight: this.lgDevice
                                                                    ? `${this.contentHeight}px`
                                                                    : undefined,
                                                                height: this.smDevice
                                                                    ? `${this.contentHeight}px`
                                                                    : undefined,
                                                            },
                                                        },
                                                        [this.$slots.body]
                                                    )
                                                  : null,
                                              this.$slots.footer
                                                  ? h(
                                                        'div',
                                                        {
                                                            class: [
                                                                'modal-footer',
                                                                {
                                                                    'modal-footer-mobile':
                                                                        this.smDevice,
                                                                },
                                                            ],
                                                            ref: 'modalFooter',
                                                        },
                                                        [this.$slots.footer]
                                                    )
                                                  : null,
                                          ]
                                      ),
                                  ]
                              ),
                          ]
                      ),
                  ]
              )
            : null
    },
}
</script>
